import { useId } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  BarChart3 as BarChart3Icon,
  Inbox as InboxIcon,
  LayoutTemplate as LayoutTemplateIcon,
  Megaphone as MegaphoneIcon,
  MessageSquareText as PostCommentsIcon,
  Radio as RadioIcon,
  Settings as SettingsIcon,
  UserCog as TeamIcon,
  Users as UsersIcon,
  type LucideIcon,
} from "lucide-react";
import { canAccessTeam, type OrganizationMemberRoleName } from "@multi-chat/shared";
import { useOrganizationBetaFeatures } from "../../features/beta-features/use-organization-beta-features.js";
import { InboxRailUnreadBadge } from "../../features/inbox/InboxRailUnreadBadge.js";
import { SimpleTooltip } from "../ui/tooltip/index.js";
import { cn } from "../../lib/utils.js";
import { motion, useReducedMotion } from "../../lib/motion.js";
import { RAIL_FOCUS, railActiveIndicatorClassName } from "./rail-compact.js";
import { isRailItemActive, RAIL_TEAM_PATH, RAIL_TEMPLATES_PATH } from "./rail-nav-active.js";

interface RailItem {
  to: string;
  label: string;
  icon: LucideIcon;
}

interface RailGroup {
  id: "workspace" | "manage";
  heading: string;
  items: RailItem[];
}

const SECTION_HEADING =
  "m-0 px-[11px] pt-[9px] pb-1.5 text-[10px] leading-[1.5] font-[750] tracking-[0.08em] text-nav-section";

const ROW = `relative my-0.5 flex min-h-11 w-full items-center rounded-[9px] text-start font-[650] no-underline transition-[background-color,color] duration-[160ms] ${RAIL_FOCUS}`;
const ROW_EXPANDED = "gap-[13px] px-[13px] py-3";
const ROW_COMPACT = "justify-center p-[13px]";
const ROW_IDLE = "text-nav-text hover:bg-[rgba(127,216,182,0.09)] hover:text-white";
const ROW_ACTIVE =
  "bg-[linear-gradient(90deg,rgba(66,165,129,0.12),rgba(99,193,154,0.19))] text-[#f6fffb]";

function RailNavLink({
  item,
  active,
  compact,
  reduceMotion,
  onNavigate,
  badge,
}: {
  item: RailItem;
  active: boolean;
  compact: boolean;
  reduceMotion: boolean;
  onNavigate?: () => void;
  badge?: React.ReactNode;
}): React.JSX.Element {
  const Icon = item.icon;
  const link = (
    <Link
      to={item.to}
      onClick={onNavigate}
      aria-current={active ? "page" : undefined}
      aria-label={compact ? item.label : undefined}
      className={cn(
        ROW,
        compact ? ROW_COMPACT : ROW_EXPANDED,
        active ? ROW_ACTIVE : ROW_IDLE,
        compact && badge ? "relative" : null,
      )}
    >
      {active ? (
        <motion.span
          layoutId="rail-active-indicator"
          className={railActiveIndicatorClassName(compact)}
          transition={{ duration: reduceMotion ? 0 : 0.16, ease: "easeOut" }}
          aria-hidden="true"
        />
      ) : null}
      <Icon
        size={compact ? 22 : 19}
        strokeWidth={1.8}
        className={cn("shrink-0", active && "text-[#6addb1]")}
        aria-hidden="true"
      />
      {compact ? null : (
        <motion.span
          initial={reduceMotion ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: reduceMotion ? 0 : 0.14 }}
          className="min-w-0 truncate"
        >
          {item.label}
        </motion.span>
      )}
      {badge}
    </Link>
  );

  return compact ? <SimpleTooltip content={item.label}>{link}</SimpleTooltip> : link;
}

export function AppRailNav({
  compact,
  role,
  onNavigate,
}: {
  compact: boolean;
  role: OrganizationMemberRoleName;
  onNavigate?: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { pathname, search } = useLocation();
  const headingId = useId();
  const reduceMotion = useReducedMotion() ?? false;
  const { hasBetaFeature } = useOrganizationBetaFeatures();
  const canManageWorkspace = role === "OWNER" || role === "ADMIN";
  const canViewReports = canManageWorkspace;
  const postCommentsEnabled = hasBetaFeature("POST_COMMENTS");

  const workspace: RailItem[] = [
    { to: "/inbox", label: t("app.nav.inbox"), icon: InboxIcon },
    ...(postCommentsEnabled
      ? [{ to: "/post-comments", label: t("app.nav.postComments"), icon: PostCommentsIcon }]
      : []),
    ...(canManageWorkspace
      ? [
          { to: "/contacts", label: t("app.nav.contacts"), icon: UsersIcon },
          { to: "/campaigns", label: t("app.nav.campaigns"), icon: MegaphoneIcon },
          ...(canViewReports
            ? [{ to: "/reports", label: t("app.nav.reports"), icon: BarChart3Icon }]
            : []),
          { to: "/channels", label: t("app.nav.channels"), icon: RadioIcon },
          { to: RAIL_TEMPLATES_PATH, label: t("app.nav.templates"), icon: LayoutTemplateIcon },
        ]
      : []),
  ];
  const manage: RailItem[] = canAccessTeam(role)
    ? [
        { to: "/settings", label: t("app.nav.settings"), icon: SettingsIcon },
        { to: RAIL_TEAM_PATH, label: t("app.account.team"), icon: TeamIcon },
      ]
    : [];
  const groups: RailGroup[] = [
    { id: "workspace", heading: t("app.sidebar.workspaceHeading"), items: workspace },
    { id: "manage", heading: t("app.sidebar.manageHeading"), items: manage },
  ];

  return (
    <nav className="flex min-w-0 flex-col" aria-label={t("app.name")}>
      {groups.map((group) =>
        group.items.length === 0 ? null : (
          <div key={group.id} role="group" aria-labelledby={`${headingId}-${group.id}`}>
            <p id={`${headingId}-${group.id}`} className={compact ? "sr-only" : SECTION_HEADING}>
              {group.heading}
            </p>
            {group.items.map((item) => (
              <RailNavLink
                key={item.to}
                item={item}
                active={isRailItemActive(pathname, item.to, search)}
                compact={compact}
                reduceMotion={reduceMotion}
                onNavigate={onNavigate}
                badge={
                  item.to === "/inbox" ? <InboxRailUnreadBadge compact={compact} /> : null
                }
              />
            ))}
          </div>
        ),
      )}
    </nav>
  );
}
